import React, { useEffect, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, List, Lock, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import type { TopicNoteReaderResponse } from "../../lib/api";
import { PREMIUM_ROUTE } from "../../lib/constants";
import { useStudent } from "../../context/StudentContext";

function extractText(node: any): string {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (node.props && node.props.children) return extractText(node.props.children);
  return "";
}

export interface ShortNotesReaderProps {
  data: TopicNoteReaderResponse;
  onNavigate?: (topicId: string) => void;
  onFinish?: () => void;
  onUpgrade?: () => void;
  backUrl?: string;
  isReadOnlyPreview?: boolean;
  hideBackLink?: boolean;
  onToggleOutline?: () => void;
  semester?: number | null;
}

export function ShortNotesReader({
  data,
  onNavigate,
  onFinish,
  onUpgrade,
  backUrl = "/student/study",
  isReadOnlyPreview = false,
  hideBackLink = false,
  onToggleOutline,
  semester,
}: ShortNotesReaderProps) {
  const { hasPendingRequest, openUpgrade } = useStudent();
  const isPending = hasPendingRequest(semester);
  const { chapter, topic, position, prev, next, module: moduleInfo } = data;

  const [fontSize, setFontSize] = useState<"sm" | "base" | "lg">(() => {
    try {
      const stored = localStorage.getItem("soorgaa_reader_font_size");
      if (stored === "sm" || stored === "base" || stored === "lg") return stored;
    } catch {}
    return "base";
  });

  const [readingProgress, setReadingProgress] = useState(0);

  const changeFontSize = (nextSize: "sm" | "base" | "lg") => {
    setFontSize(nextSize);
    try {
      localStorage.setItem("soorgaa_reader_font_size", nextSize);
    } catch {}
  };

  // Track scroll reading progress and save resume position
  useEffect(() => {
    function handleScroll() {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total <= 0) {
        setReadingProgress(0);
        return;
      }
      const currentScroll = window.scrollY;
      setReadingProgress(Math.min(100, Math.max(0, (currentScroll / total) * 100)));
      if (topic?.id) {
        try {
          localStorage.setItem(`soorgaa_reader_pos_${topic.id}`, String(currentScroll));
        } catch {}
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [topic?.id]);

  // Restore scroll position where student left off
  useEffect(() => {
    if (topic?.id) {
      try {
        const savedPos = localStorage.getItem(`soorgaa_reader_pos_${topic.id}`);
        if (savedPos) {
          const scrollY = parseFloat(savedPos);
          if (!isNaN(scrollY) && scrollY > 0) {
            window.scrollTo({ top: scrollY, behavior: "smooth" });
          }
        }
      } catch {}
    }
  }, [topic?.id]);

  // Keyboard navigation: left arrow for prev, right arrow for next
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isReadOnlyPreview) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (e.key === "ArrowLeft" && prev && onNavigate) {
        e.preventDefault();
        onNavigate(prev.topicId);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (next && onNavigate) {
          onNavigate(next.topicId);
        } else if (!next && onFinish) {
          onFinish();
        }
      }
    },
    [prev, next, onNavigate, onFinish, isReadOnlyPreview]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const katexOptions = {
    throwOnError: false,
    strict: false,
    errorColor: "var(--destructive)",
  };

  const nextLabel = next
    ? next.startsNewChapter
      ? `Next chapter: ${next.chapterTitle}`
      : "Next"
    : "Finish";

  const fontSizeClasses = {
    sm: "text-sm leading-relaxed",
    base: "text-base leading-[1.7]",
    lg: "text-lg leading-[1.8]",
  }[fontSize];

  return (
    <article className="short-notes-reader w-full max-w-[65ch] mx-auto pb-24 sm:pb-8">
      {/* Sticky Reading Progress Bar */}
      <div
        className="fixed top-0 left-0 right-0 z-40 h-1 bg-muted/40"
        role="progressbar"
        aria-valuenow={Math.round(readingProgress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Reading progress"
      >
        <div
          className="bg-primary h-full transition-all duration-100 ease-out"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            {!hideBackLink && !isReadOnlyPreview ? (
              <Link
                to={backUrl}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded"
                aria-label={`Back to ${moduleInfo?.title || "module"}`}
              >
                <ArrowLeft size={14} aria-hidden="true" />
                <span>Back to {moduleInfo?.title || "module"}</span>
              </Link>
            ) : onToggleOutline ? (
              <button
                type="button"
                onClick={onToggleOutline}
                className="lg:hidden inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-sm bg-secondary hover:bg-secondary/80 text-foreground border border-border transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                aria-label="Open chapters & topics outline"
              >
                <List size={14} aria-hidden="true" />
                <span>Outline</span>
              </button>
            ) : (
              <span className="text-xs font-medium text-muted-foreground">
                {isReadOnlyPreview ? "Preview mode" : ""}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2.5">
            {/* Font Size Controls */}
            <div className="flex items-center gap-1 bg-secondary/80 rounded-md p-0.5 border border-border">
              <button
                type="button"
                onClick={() => changeFontSize(fontSize === "lg" ? "base" : "sm")}
                disabled={fontSize === "sm"}
                className="px-2 py-0.5 rounded text-xs font-semibold hover:bg-card disabled:opacity-40 transition cursor-pointer"
                aria-label="Decrease font size"
              >
                A−
              </button>
              <span className="text-xs text-muted-foreground px-1 font-mono uppercase">{fontSize}</span>
              <button
                type="button"
                onClick={() => changeFontSize(fontSize === "sm" ? "base" : "lg")}
                disabled={fontSize === "lg"}
                className="px-2 py-0.5 rounded text-xs font-semibold hover:bg-card disabled:opacity-40 transition cursor-pointer"
                aria-label="Increase font size"
              >
                A+
              </button>
            </div>

            {position && position.total > 0 && (
              <span
                className="text-xs font-semibold text-muted-foreground bg-secondary px-2.5 py-1 rounded-full tabular-nums"
                aria-label={`Topic position ${position.current} of ${position.total}`}
              >
                {`${position.current} / ${position.total}`}
              </span>
            )}
          </div>
        </div>

        {chapter?.title && (
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
            {chapter.title}
          </p>
        )}

        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
          {topic?.title}
        </h1>
      </header>

      {/* Body: Markdown with math support or Get Premium gate */}
      {data.isLocked || data.requiresPremium || data.access === "locked" ? (
        <div className="my-8 p-6 sm:p-8 rounded-xl border border-warning/30 bg-warning/5 text-center space-y-4 shadow-xs">
          <div className="w-12 h-12 rounded-full bg-warning/10 text-warning flex items-center justify-center mx-auto">
            <Lock size={24} />
          </div>
          <div className="space-y-1.5">
            <h3 className="text-lg font-bold text-foreground">Premium Chapter Notes</h3>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto">
              This chapter is exclusive to Soorgaa Premium. Free tier students can study Chapter 1 notes. Upgrade your account to unlock all chapters, notes, and interactive exercises!
            </p>
          </div>
          <div className="pt-2">
            {isPending ? (
              <span className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-muted text-muted-foreground font-bold cursor-default text-sm border border-border">
                <Clock size={16} />
                <span>Awaiting review</span>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (onUpgrade) onUpgrade();
                  else openUpgrade({ semester });
                }}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-bold hover:opacity-90 shadow-md transition cursor-pointer text-sm"
              >
                <Sparkles size={16} />
                <span>{semester ? `Unlock Semester ${semester}` : "Unlock Semester"}</span>
              </button>
            )}
          </div>
        </div>
      ) : (
      <div className={`notes-body max-w-prose ${fontSizeClasses} text-foreground space-y-4`}>
        {topic?.notes?.trim() ? (
          <ReactMarkdown
            skipHtml={true}
            remarkPlugins={[remarkMath, remarkGfm]}
            rehypePlugins={[[rehypeKatex, katexOptions]]}
            components={{
              blockquote: ({ children }) => {
                const textContent = extractText(children).trim();

                // Callout: blockquote starting with "Important:"
                if (/^important:\s*/i.test(textContent)) {
                  return (
                    <blockquote
                      role="note"
                      aria-label="Important notice"
                      className="my-4 border-l-4 border-primary bg-primary/5 pl-4 pr-3 py-3 rounded-r text-sm text-foreground not-italic space-y-1"
                    >
                      <span className="font-semibold text-primary block text-xs uppercase tracking-wider">
                        Important
                      </span>
                      <div className="[&>p]:my-1">{children}</div>
                    </blockquote>
                  );
                }

                // Callout: blockquote starting with "Example:" or "Example"
                if (/^example(?::|\b)/i.test(textContent)) {
                  return (
                    <div
                      role="region"
                      aria-label="Example section"
                      className="my-4 border border-border bg-card/60 p-4 rounded-md text-sm text-foreground space-y-1"
                    >
                      <span className="font-semibold text-muted-foreground block text-xs uppercase tracking-wider">
                        Example
                      </span>
                      <div className="[&>p]:my-1">{children}</div>
                    </div>
                  );
                }

                // Standard blockquote
                return (
                  <blockquote className="my-4 border-l-2 border-border pl-4 italic text-muted-foreground">
                    {children}
                  </blockquote>
                );
              },
              table: ({ children }) => (
                <div
                  tabIndex={0}
                  role="region"
                  aria-label="Scrollable data table"
                  className="my-4 overflow-x-auto w-full rounded border border-border focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                >
                  <table className="min-w-full divide-y divide-border text-sm text-left">
                    {children}
                  </table>
                </div>
              ),
              th: ({ children }) => (
                <th scope="col" className="bg-secondary px-3.5 py-2.5 font-semibold text-foreground border-b border-border">
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td className="px-3.5 py-2.5 text-foreground border-b border-border/50">
                  {children}
                </td>
              ),
              h2: ({ children }) => (
                <h2 className="text-xl font-bold mt-6 mb-2 text-foreground tracking-tight">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-lg font-semibold mt-4 mb-2 text-foreground tracking-tight">
                  {children}
                </h3>
              ),
              ul: ({ children }) => (
                <ul className="list-disc pl-5 my-3 space-y-1.5">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="list-decimal pl-5 my-3 space-y-1.5">{children}</ol>
              ),
              p: ({ children }) => (
                <p className="my-3 leading-relaxed text-foreground">{children}</p>
              ),
              pre: ({ children }) => (
                <div className="my-3 overflow-x-auto rounded bg-secondary p-3 text-xs font-mono text-foreground border border-border">
                  <pre>{children}</pre>
                </div>
              ),
              code: ({ inline, children, ...props }: any) =>
                inline ? (
                  <code
                    className="bg-secondary px-1.5 py-0.5 rounded text-xs font-mono text-foreground border border-border/60"
                    {...props}
                  >
                    {children}
                  </code>
                ) : (
                  <code {...props}>{children}</code>
                ),
            }}
          >
            {topic.notes}
          </ReactMarkdown>
        ) : (
          <p className="text-sm text-muted-foreground italic">No notes yet.</p>
        )}

        {/* Key terms */}
        {topic.keyTerms && topic.keyTerms.length > 0 && !/key\s*terms/i.test(topic.notes || "") && (
          <div className="mt-8 pt-6 border-t border-border">
            <h3 className="text-base font-semibold text-foreground mb-3">Key terms</h3>
            <dl className="space-y-2 text-sm leading-relaxed">
              {topic.keyTerms.map((kt, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:gap-2">
                  <dt className="font-bold text-foreground">{kt.term}:</dt>
                  <dd className="text-muted-foreground">{kt.definition}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
      )}

      {/* Navigation: Fixed bottom on mobile, inline after content on desktop */}
      {!isReadOnlyPreview && (
        <nav
          aria-label="Topic navigation"
          className="fixed bottom-0 left-0 right-0 p-3 bg-background/95 backdrop-blur border-t border-border z-30 sm:static sm:p-0 sm:bg-transparent sm:border-0 sm:mt-10"
        >
          <div className="max-w-[65ch] mx-auto flex items-center justify-between gap-3">
            {prev ? (
              <button
                type="button"
                onClick={() => onNavigate && onNavigate(prev.topicId)}
                aria-label={`Previous topic: ${prev.title}`}
                className="px-4 py-2.5 rounded border border-border bg-card hover:bg-secondary text-sm font-medium text-foreground transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
              >
                Previous
              </button>
            ) : (
              <button
                type="button"
                disabled
                aria-label="No previous topic"
                className="px-4 py-2.5 rounded border border-border/40 bg-secondary/40 text-muted-foreground/40 text-sm font-medium cursor-not-allowed"
              >
                Previous
              </button>
            )}

            {next ? (
              <button
                type="button"
                onClick={() => onNavigate && onNavigate(next.topicId)}
                aria-label={
                  next.startsNewChapter
                    ? `Next chapter: ${next.chapterTitle}`
                    : `Next topic: ${next.title}`
                }
                className="px-4 py-2.5 rounded bg-primary text-primary-foreground hover:opacity-90 text-sm font-medium transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
              >
                {nextLabel}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onFinish && onFinish()}
                aria-label="Finish and return to module"
                className="px-4 py-2.5 rounded bg-primary text-primary-foreground hover:opacity-90 text-sm font-medium transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
              >
                Finish
              </button>
            )}
          </div>
        </nav>
      )}
    </article>
  );
}

export function ShortNotesReaderSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-label="Loading notes"
      className="max-w-[65ch] mx-auto px-4 py-8 animate-pulse space-y-6"
    >
      <div className="flex justify-between items-center">
        <div className="h-4 w-28 bg-secondary rounded" />
        <div className="h-5 w-14 bg-secondary rounded-full" />
      </div>
      <div className="space-y-2 pt-2">
        <div className="h-3 w-24 bg-secondary rounded" />
        <div className="h-8 w-3/4 bg-secondary rounded" />
      </div>
      <div className="space-y-3 pt-4">
        <div className="h-4 w-full bg-secondary rounded" />
        <div className="h-4 w-5/6 bg-secondary rounded" />
        <div className="h-4 w-4/6 bg-secondary rounded" />
      </div>
      <div className="h-24 w-full bg-secondary/60 rounded border border-border/50" />
      <div className="space-y-3">
        <div className="h-4 w-full bg-secondary rounded" />
        <div className="h-4 w-3/4 bg-secondary rounded" />
      </div>
    </div>
  );
}

export function ShortNotesReaderError({
  error,
  onRetry,
  backUrl,
}: {
  error: string;
  onRetry: () => void;
  backUrl?: string;
}) {
  return (
    <div className="max-w-[65ch] mx-auto px-4 py-16 text-center space-y-4">
      <div className="p-6 rounded-lg border border-border bg-card space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Could not load notes</h2>
        <p className="text-sm text-muted-foreground">
          {error || "An error occurred while loading this topic."}
        </p>
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            type="button"
            onClick={onRetry}
            className="px-4 py-2 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            aria-label="Retry loading notes"
          >
            Retry
          </button>
          {backUrl && (
            <Link
              to={backUrl}
              className="px-4 py-2 rounded border border-border text-sm font-medium hover:bg-secondary transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
              aria-label="Return to module"
            >
              Back to module
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export function ShortNotesReaderEmpty({
  backUrl,
  onUpgrade,
  semester,
}: {
  backUrl?: string;
  onUpgrade?: () => void;
  semester?: number | null;
}) {
  const { hasPendingRequest, openUpgrade } = useStudent();
  const isPending = hasPendingRequest(semester);

  return (
    <div className="max-w-[65ch] mx-auto px-4 py-16 text-center space-y-4">
      <div className="p-8 rounded-xl border border-dashed border-border bg-card space-y-4">
        <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
          <Sparkles size={24} />
        </div>
        <h2 className="text-lg font-bold text-foreground">Unlock Full Study Notes</h2>
        <p className="text-xs sm:text-sm text-muted-foreground max-w-sm mx-auto">
          Study notes for this topic are included with Soorgaa Premium. Upgrade now to access all curriculum notes and materials.
        </p>
        <div className="flex items-center justify-center gap-3 pt-2 flex-wrap">
          {isPending ? (
            <span className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-muted text-muted-foreground font-bold cursor-default text-xs border border-border">
              <Clock size={14} />
              <span>Awaiting review</span>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (onUpgrade) onUpgrade();
                else openUpgrade({ semester });
              }}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-primary text-primary-foreground font-bold hover:opacity-90 shadow-xs transition cursor-pointer text-xs"
            >
              <Sparkles size={14} />
              <span>{semester ? `Unlock Semester ${semester}` : "Unlock Semester"}</span>
            </button>
          )}
          {backUrl && (
            <Link
              to={backUrl}
              className="px-4 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-secondary transition inline-block focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
              aria-label="Return to module"
            >
              Back to module
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
